import { useLocation } from 'react-router';
import type { PAGE_POSITIONS, PageType } from 'types';
import type {
  Affiliate,
  Price,
  ProductListItemExternal
} from 'types/capi/shared';
import { fromProductListItem } from 'types/TrackableProduct';

import type { Variant } from '~/components/Button';
import AffiliateBuyNowEvent from '~/events/AffiliateBuyNowEvent';
import ProductCardClickEvent from '~/events/ProductCardClickEvent';
import type { ProductCardDisplayType } from '~/events/ProductCardInteractionEvent';
import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Tracked from '../Tracked';

type Props = {
  affiliate: Affiliate;
  buttonPosition: number;
  children: JSX.Element | JSX.Element[] | string;
  className?: string;
  displayType?: ProductCardDisplayType;
  listId?: string | null;
  modulePosition?: number;
  moduleType: string;
  name?: TrackedButtonClickName;
  openPdp?: boolean;
  pagePosition: PAGE_POSITIONS;
  pageType?: PageType;
  position: number;
  price: Price;
  product: ProductListItemExternal;
  selectedSize?: string;
  urlTemplates: UrlTemplates;
  variant?: Variant;
};

export default function ClickOutLink({
  affiliate,
  buttonPosition,
  children,
  listId,
  displayType,
  modulePosition,
  moduleType,
  name,
  className,
  position,
  openPdp = true,
  pagePosition,
  price,
  product,
  pageType,
  urlTemplates,
  variant
}: Props) {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const interstitialUrl =
    env.SITE_BASE_URL + `/co/${product.id}/${affiliate.id}`;

  const fireEvents = async (url: string, clickRef: string) => {
    if (isBot) return;

    new ProductCardClickEvent({
      isBot,
      isClickOut: true,
      listId,
      pagePosition,
      pageType,
      position,
      product,
      user
    }).send();

    new ProductCardInteractionEvent({
      displayType,
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      modulePosition,
      moduleType,
      position,
      product,
      retailerId: affiliate.canonical_id
    }).send();

    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product,
      session,
      url
    });

    await new AffiliateBuyNowEvent({
      affiliateId: affiliate.id,
      affiliateName: affiliate.name,
      buttonPosition,
      clientIp,
      currency: price.currency,
      isBot,
      isReseller: affiliate.is_reseller,
      pagePath,
      pagePosition,
      price: price.sale ?? price.regular,
      product: fromProductListItem(product),
      queryID: localStorage.getItem('queryID'),
      user
    }).send();
  };

  const onClick = async (url: string, clickRef: string) => {
    if (openPdp) window.open(interstitialUrl, '_blank');

    await fireEvents(url, clickRef);
  };

  const { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, {
    buttonPosition,
    user
  });

  if (!openPdp) {
    return (
      <Tracked.ExternalLink
        affiliate={affiliate.name}
        variant={variant ?? 'none'}
        title={product.name}
        name={name ?? 'PRODUCT_CARD'}
        pagePosition={pagePosition}
        aria-label={product.name}
        href={url}
        onClick={() => onClick(url, clickRef)}
        className={className}
        target="_blank"
        rel="sponsored nofollow noopener"
      >
        {children}
      </Tracked.ExternalLink>
    );
  }

  return (
    <Tracked.InternalLink
      affiliate={affiliate.name}
      variant={variant ?? 'none'}
      title={product.name}
      name={name ?? 'PRODUCT_CARD'}
      pagePosition={pagePosition}
      aria-label={product.name}
      to={product.path}
      onClick={() => onClick(url, clickRef)}
      className={className}
    >
      {children}
    </Tracked.InternalLink>
  );
}
